import { useState, useEffect, useCallback } from 'react';
import { FiClock, FiChevronLeft, FiChevronRight, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import API_URL from '../config';

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}
function hoursLabel(h) {
  if (!h) return '—';
  const hrs = Math.floor(h);
  const min = Math.round((h - hrs) * 60);
  return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
}
function weekStart(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Convert a datetime string to "YYYY-MM-DDTHH:MM" for <input type="datetime-local">
function toLocalInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Attendance({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [editing, setEditing] = useState(null); // { id, clock_in, clock_out }
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const ws = weekStart(weekOffset);
  const we = new Date(ws); we.setDate(we.getDate() + 6);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const from = ws.toISOString().split('T')[0];
      const to   = we.toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/attendance/report?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setRecords(d.records || []);
    } catch {} finally { setLoading(false); }
  }, [token, weekOffset]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const totalHours = records.reduce((sum, r) => sum + (r.worked_hours || 0), 0);

  function openEdit(r) {
    setEditError('');
    setEditing({
      id: r.id,
      clock_in: toLocalInput(r.clock_in),
      clock_out: toLocalInput(r.clock_out),
    });
  }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true);
    setEditError('');
    try {
      const body = { clock_in: editing.clock_in ? new Date(editing.clock_in).toISOString() : undefined };
      if (editing.clock_out) body.clock_out = new Date(editing.clock_out).toISOString();
      else body.clock_out = null;

      const res = await fetch(`${API_URL}/api/attendance/${editing.id}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setEditError(data.error || 'Failed to save'); return; }
      setEditing(null);
      fetchAttendance();
    } catch { setEditError('Network error'); } finally { setSaving(false); }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Weekly Attendance</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekOffset(v => v - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <FiChevronLeft size={18} />
          </button>
          <span className="text-sm text-slate-600 min-w-[140px] text-center">
            {fmtDate(ws)} – {fmtDate(we)}
          </span>
          <button onClick={() => setWeekOffset(v => v + 1)} disabled={weekOffset >= 0} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30">
            <FiChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
        <FiClock size={16} className="text-blue-500" />
        <span className="text-sm text-blue-700">
          Total hours this week: <strong>{hoursLabel(totalHours)}</strong>
        </span>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-800">Edit Attendance</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><FiX size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Clock In</label>
                <input
                  type="datetime-local"
                  value={editing.clock_in}
                  onChange={e => setEditing(v => ({ ...v, clock_in: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Clock Out <span className="text-slate-400 font-normal">(leave empty if still clocked in)</span></label>
                <input
                  type="datetime-local"
                  value={editing.clock_out}
                  onChange={e => setEditing(v => ({ ...v, clock_out: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {editError && <p className="text-xs text-red-500">{editError}</p>}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={saveEdit} disabled={saving} className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                <FiCheck size={15} />{saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Employee</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Date</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Clock In</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Clock Out</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Hours</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No attendance records for this week.</td></tr>
            )}
            {!loading && records.map((r, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{r.employee}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(r.clock_in)}</td>
                <td className="px-4 py-3 text-slate-700">{fmt(r.clock_in)}</td>
                <td className="px-4 py-3 text-slate-700">
                  {r.clock_out ? fmt(r.clock_out) : <span className="text-amber-500 text-xs font-medium">Missing</span>}
                </td>
                <td className="px-4 py-3 font-medium text-slate-800">{hoursLabel(r.worked_hours)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => openEdit(r)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    title="Edit"
                  >
                    <FiEdit2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

